// ---------------------------------------------------------------------------
// Wire schema. This is the single description of what getPolicyMarketData
// returns; it must match the dataset assembled in functions/src/index.js.
// ---------------------------------------------------------------------------

/** A market week as aggregated by aggregateMarketWeeks. */
export interface MarketWeek {
  id: string;
  weekStart: string;
  weekEnd: string;
  highClose: number;
  lowClose: number;
  close: number;
  spread: number;
  spreadPercent: number;
  observationCount: number;
  firstObservation: string;
  lastObservation: string;
  marketSeries: string;
  rangeDefinition: string;
  /** Place on the shared 1–10 axis, scaled server-side; null when unscalable. */
  score: number | null;
  /** The band it was scored against — the previous and current years' extremes. */
  scaleLow: number | null;
  scaleHigh: number | null;
  scaleBasis: 'previous_and_own_fiscal_year' | 'own_fiscal_year';
}

/** A market week that has additionally been aligned to the FOMC calendar. */
export interface PolicyWeek extends MarketWeek {
  policyPhase: 'meeting_week' | 'pre_policy' | 'post_policy' | 'between_meetings';
  meetingId: string | null;
  meetingDate: string | null;
  meetingLabel: string | null;
  daysToMeeting: number | null;
  daysSinceMeeting: number | null;
}

export interface Meeting {
  id: string;
  year: number;
  monthLabel: string;
  dateLabel: string;
  startDate: string;
  endDate: string;
  hasEconomicProjections: boolean;
  isNotationVote: boolean;
  isEmergency: boolean;
  statement?: string | null;
  minutes?: string;
  pressConference?: string;
  implementationNote?: string;
  sourceUrl: string;
}

export interface RateAction {
  date: string;
  increaseBps: number;
  decreaseBps: number;
  direction: 'increase' | 'decrease';
  basisPoints: number;
  level: string;
  emergency: boolean;
  sourceUrl: string;
}

export interface BojMeeting {
  id: string;
  startDate: string;
  endDate: string;
  sourceUrl: string;
}

/** A BIS policy-rate change, as produced by parsePolicyRateChanges. */
export interface PolicyRateChange {
  date: string;
  direction: 'increase' | 'decrease';
  percentagePoints: number;
  previousRate: number;
  newRate: number;
  sourceUrl: string;
}

export interface InflationTarget {
  year: number;
  measure: string;
  lowerBound: number;
  upperBound: number;
  midpoint: number;
  unit: 'percent';
  framework: string;
  sourceUrl: string;
}

export interface EggPriceIndex {
  name: string;
  geography: string;
  adjustment?: 'Original';
  unit: 'index points';
  frequency: 'quarterly' | 'monthly';
  referenceBasePeriod: string;
  sourceUrl?: string;
  sourceUrls?: string[];
  observations: Array<{period: string; value: number; monthlyChange?: number}>;
  yearly: Array<{
    year: number;
    value: number;
    quartersReported?: number;
    monthsReported?: number;
    completeYear: boolean;
    method: string;
  }>;
}

export interface HousePriceIndex {
  name: string;
  geography: string;
  unit: 'index points';
  frequency: 'quarterly';
  referenceBasePeriod: string;
  sourceUrl: string;
  observations: Array<{period: string; value: number}>;
  yearly: Array<{
    year: number;
    value: number;
    quartersReported: number;
    completeYear: boolean;
    method: string;
  }>;
}

export interface RbaFiscalIndicators {
  inflationTarget?: InflationTarget;
  eggPriceIndex?: {
    value: number;
    quartersReported: number;
    completeFiscalYear: boolean;
    referenceBasePeriod: string;
  };
  housePriceIndex?: {
    value: number;
    quartersReported: number;
    completeFiscalYear: boolean;
    referenceBasePeriod: string;
  };
}

export interface BcbFiscalIndicators {
  inflationTarget?: InflationTarget;
  eggPriceIndex?: {
    value: number;
    monthsReported: number;
    completeFiscalYear: boolean;
    referenceBasePeriod: string;
  };
  housePriceIndex?: {
    value: number;
    quartersReported: number;
    completeFiscalYear: boolean;
    referenceBasePeriod: string;
  };
}

/** A BOJ rate change, attributed back to the statement that decided it. */
export interface BojRateAction extends PolicyRateChange {
  effectiveDate: string;
  statementTitle: string | null;
}

export interface PolicyDataset {
  generatedAt: string;
  calendarSource: string;
  rateActionsSource: string;
  marketSource: string;
  marketSymbol: string;
  marketDescription: string;
  goldSource: string;
  goldSymbol: string;
  goldDescription: string;
  oilSource: string;
  oilSymbol: string;
  oilDescription: string;
  unemploymentSource: string;
  unemploymentSymbol: string;
  unemploymentDescription: string;
  dollarSource: string;
  dollarSymbol: string;
  dollarDescription: string;
  policyRatesSource: string;
  policyRatesDescription: string;
  australiaPolicySource: string;
  australiaInflationTargetSource: string;
  australiaInflationTargets: InflationTarget[];
  australiaEggPriceIndexSource: string;
  australiaEggPriceIndex: EggPriceIndex;
  australiaHousePriceIndexSource: string;
  australiaHousePriceIndex: HousePriceIndex;
  brazilPolicySource: string;
  brazilInflationTargetSource: string;
  brazilInflationTargets: InflationTarget[];
  brazilEggPriceIndexSources: string[];
  brazilEggPriceIndex: EggPriceIndex;
  brazilHousePriceIndexSource: string;
  brazilHousePriceIndex: HousePriceIndex;
  bojCalendarSource: string;
  bojPastCalendarSource: string;
  earliestCalendarYear: number;
  earliestFiscalYear: number;
  meetingCount: number;
  marketWeekCount: number;
  rangeDefinition: string;
  scaleDefinition: string;
  meetings: Meeting[];
  rateActions: RateAction[];
  weeks: PolicyWeek[];
  goldWeeks: MarketWeek[];
  oilWeeks: MarketWeek[];
  unemploymentWeeks: MarketWeek[];
  dollarWeeks: MarketWeek[];
  bojMeetings: BojMeeting[];
  bojRateActions: BojRateAction[];
  /** Every BIS policy series, keyed by reference area — 'AU', 'XM', and so on. */
  policyRateChanges: Record<string, PolicyRateChange[]>;
}

// ---------------------------------------------------------------------------
// View models. Derived from the schema above for rendering; never sent over
// the wire.
// ---------------------------------------------------------------------------

/**
 * A bank's key in the registry — 'FED', 'BOE', 'BANXICO'. Free-form rather than a
 * union: the roster follows what BIS publishes, so it is data, not a type.
 */
export type EventBank = string;

/** The subset read from BIS, which is every bank but the Fed and the BOJ. */
export type PolicyBank = EventBank;

/** One dated policy event listed under its month beneath the chart. */
export interface PolicyEvent {
  bank: EventBank;
  date: string;
  dateLabel: string;
  changeLabel?: string;
  /** Signed size of the move in percentage points; absent when nothing changed. */
  changePoints?: number;
  /** True for scheduled meetings (Fed, BOJ), false for a bare rate change. */
  isMeeting: boolean;
  future: boolean;
  hasProjections: boolean;
  emergency: boolean;
}

/** A month's worth of policy events, rendered as one column under the chart. */
export interface MonthColumn {
  month: number;
  label: string;
  events: PolicyEvent[];
}

/** Signed percentage-point move as a label, e.g. 0.85 → '↑0.85%', 0 → '0%'. */
export const pointsLabel = (points: number): string =>
  points === 0 ? '0%' : `${points > 0 ? '↑' : '↓'}${Math.abs(points)}%`;

/** One bank's rate in effect at the end of each fiscal-year month, Oct → Sep. */
export interface MonthRateRow {
  bank: EventBank;
  levels: (string | null)[];
  /** The same rates as numbers so they can be totalled; the Fed's is its range midpoint. */
  values: (number | null)[];
}

/** A policy event's tick and marker on the chart itself. */
export interface EventMarker {
  x: number;
  bank: EventBank;
  date: string;
  emergency: boolean;
  future: boolean;
  /** Calendar month, 1–12, so a marker can be paired with its rate-table column. */
  month: number;
  /** Whether this event moved the rate; a meeting that held it did not. */
  changed: boolean;
}

/** The market feeds the chart can draw. */
export type MarketSeriesKey = 'spy' | 'gold' | 'oil' | 'unemployment' | 'dollar';

/** How to find one feed in the dataset and how to present it. */
export interface MarketSeriesDefinition {
  key: MarketSeriesKey;
  /** Legend text. */
  label: string;
  /** Where the feed comes from, shown as the toggle's tooltip. */
  description: string;
  /** Shorter name for the row header stat. */
  statLabel: string;
  icon: string;
  color: string;
  dash: string | null;
  /** The reference feed: its line is thicker and carries FOMC momentum colouring. */
  primary: boolean;
  monthDots: boolean;
  dotRadius: number;
  decimals: number;
  weeksOf: (dataset: PolicyDataset) => MarketWeek[];
}

/** One feed resolved against one fiscal year. */
export interface MarketSeries {
  definition: MarketSeriesDefinition;
  low?: number;
  high?: number;
  points: ChartPoint[];
  segments: ChartSegment[];
}

/** A feed's marker at the middle of a month. */
export interface MonthDot {
  key: MarketSeriesKey;
  color: string;
  primary: boolean;
  y: number;
  score: number;
}

export interface MonthCell {
  month: number;
  x: number;
  meetingCount: number;
  hasProjections: boolean;
  dots: MonthDot[];
}

export interface ChartPoint {
  x: number;
  y: number;
  score: number;
  value: number;
  weekStart: string;
}

export interface ChartSegment {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  preFed: boolean;
  momentum: number;
  path: string;
}

export interface FedMarker {
  x: number;
  startDate: string;
  hasProjections: boolean;
  future: boolean;
  emergency: boolean;
  rateLabel?: string;
}

export interface BojMarker {
  x: number;
  startDate: string;
  future: boolean;
  rateLabel?: string;
}

export interface RateActionMarker extends RateAction {
  x: number;
}

export interface PolicyRateMarker extends PolicyRateChange {
  x: number;
  bank: PolicyBank;
}

export interface YearRow {
  year: number;
  /** Every market feed for this year, loaded by the series manager. */
  series: MarketSeries[];
  fedMarkers: FedMarker[];
  bojMarkers: BojMarker[];
  rateActionMarkers: RateActionMarker[];
  policyMarkers: PolicyRateMarker[];
  eventMarkers: EventMarker[];
  monthColumns: MonthColumn[];
  rateTable: MonthRateRow[];
  /** Every event in the year. Counts are derived from this per visible bank. */
  events: PolicyEvent[];
  upcomingCount: number;
  months: MonthCell[];
  rbaIndicators?: RbaFiscalIndicators;
  bcbIndicators?: BcbFiscalIndicators;
}

export interface DashboardData {
  years: YearRow[];
  latest?: PolicyWeek;
  widest?: PolicyWeek;
  meetingWeeks: number;
  upcomingMeetings: number;
}
